// Real-device notification-click journaller.
//
// Two entry points into `record_push_click`:
//   1. URL query params: when SW's handleNotificationClick opens a fresh
//      window (or navigates a same-origin one), it rewrites the target to
//      include ?ptrace=<uuid>&pt_tag=<tag>. We read those on mount, POST
//      the RPC, then strip the params so a refresh doesn't double-record.
//   2. SW broadcast: when SW reuses an already-open client, client.navigate
//      may be a no-op — but the SW also postMessages `push-notification-click`
//      carrying the same ptrace/tag. We record from that, too.
//
// Guardrails:
//   - Dedup per ptrace via an in-memory Set so simultaneous URL + broadcast
//     signals for the same tap don't produce two rows.
//   - Runs only in the browser. No-op on SSR / non-window contexts.
//   - Only records when signed in (RPC refuses anonymous callers). Anonymous
//     taps still work — the deep link still lands.
//
// The window of ambiguity between "tap fired" and "app mounted and signed in"
// is what pushes us to append the ptrace to the URL: even if the SPA reloads,
// signs in via the same query params, or lands in a different tab, the ptrace
// survives long enough for us to record the click. Once recorded, it is
// stripped from `window.location` via history.replaceState.

import { recordPushClick } from './push';

const recorded = new Set();

function stripPtraceFromUrl() {
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has('ptrace') && !u.searchParams.has('pt_tag')) return;
    u.searchParams.delete('ptrace');
    u.searchParams.delete('pt_tag');
    const q = u.searchParams.toString();
    const next = u.pathname + (q ? `?${q}` : '') + u.hash;
    window.history.replaceState(window.history.state, '', next);
  } catch { /* URL parse failed — leave it alone */ }
}

async function recordOnce({ tag, url, ptrace }) {
  const key = ptrace || `tag:${tag}`;
  if (!key) return null;
  if (recorded.has(key)) return null;
  recorded.add(key);
  try {
    return await recordPushClick({ tag, url, ptrace });
  } catch (e) {
    // Swallow — a failed journal write must never break the deep link.
    return { ok: false, reason: e?.message || 'record_failed' };
  }
}

let installed = false;

/**
 * Install the recorder. Idempotent — safe to call multiple times.
 * Called once from index.js at app boot.
 */
export function installPushClickRecorder() {
  if (installed) return;
  if (typeof window === 'undefined') return;
  installed = true;

  // 1) URL-carried ptrace at mount (deferred so getAccessToken() has a
  //    chance to read localStorage on cold start).
  try {
    const params = new URLSearchParams(window.location.search);
    const ptrace = params.get('ptrace');
    const tag = params.get('pt_tag');
    if (ptrace || tag) {
      setTimeout(() => {
        recordOnce({ ptrace, tag, url: window.location.pathname })
          .finally(stripPtraceFromUrl);
      }, 150);
    }
  } catch { /* no window.location.search in test envs — ignore */ }

  // 2) SW broadcast for already-open clients (client.navigate is a no-op
  //    for uncontrolled same-origin clients on some engines).
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      const msg = event?.data;
      if (!msg || msg.type !== 'push-notification-click') return;
      recordOnce({
        ptrace: msg.ptrace || null,
        tag: msg.tag || null,
        url: msg.url || null,
      });
    });
  }
}

export const _internal = { recorded, stripPtraceFromUrl, recordOnce };
